import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    CheckCircle2, CalendarCheck, CircleDot, Hourglass, AlertTriangle, Siren, StickyNote, Route as RouteIcon,
} from 'lucide-react';
import { programmeStops, stopLabel, rangeLabel, appointmentProgrammeLine } from '../../utils/careProgrammes';
import { formatDay } from '../health/healthFormat';
import { formatClock } from '../../utils/clock';

// THE STOP LINE — one programme drawn as a line of visits (owner, 21 Oct
// 2026; doc/programme-visits.md). ONE look for every place a programme is
// shown on the tenant web: the doctor's Programme card in the visit and on
// the patient's page, the desk strip, the health record's Programmes section
// and the booking question. The Start first, then Stop 1, Stop 2… each with
// the state the SERVER computed:
//
//   done      — the linked visit was completed, or the doctor ticked it
//   booked    — an appointment is linked to it (its date and time shown)
//   open      — inside its window today: book now (window dates)
//   upcoming  — its window opens later ("Opens <date>")
//   late      — its window closed with nothing booked or done
//
// An emergency booking shows its reason on the stop it took. Nothing here
// decides who may act: `actions(stop)` lets the host add buttons to a stop
// (tick / untick), and `showNotes` is off for the desk projection.

export const STOP_STYLE = {
    done: { icon: CheckCircle2, chip: 'bg-[#4ABDBD]/15 text-[#2D8A8A]', dot: 'bg-[#4ABDBD] text-white' },
    booked: { icon: CalendarCheck, chip: 'bg-[#2D8A8A]/10 text-[#2D8A8A]', dot: 'bg-[#2D8A8A] text-white' },
    open: { icon: CircleDot, chip: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500 text-white' },
    upcoming: { icon: Hourglass, chip: 'bg-gray-100 text-gray-500', dot: 'bg-gray-200 text-gray-500' },
    late: { icon: AlertTriangle, chip: 'bg-red-50 text-red-600', dot: 'bg-red-500 text-white' },
};

export function StopStateChip({ state, testId }) {
    const { t } = useTranslation();
    const key = STOP_STYLE[state] ? state : 'upcoming';
    const Icon = STOP_STYLE[key].icon;
    return (
        <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STOP_STYLE[key].chip}`}>
            <Icon className="h-3 w-3" aria-hidden="true" />
            {t(`programmes.state_${key}`)}
        </span>
    );
}

/** What a stop's state means today, in one line. */
export const stopDetail = (stop, t) => {
    const s = stop || {};
    if (s.state === 'done') {
        const date = formatDay(s.doneAt || s.appointment?.date || s.dueDate);
        if (s.kind === 'start') return date ? t('programmes.stop_started_on', { date }) : t('programmes.state_done');
        if (s.doneBy === 'tick') return date ? `${t('programmes.evidence_manual')} · ${date}` : t('programmes.evidence_manual');
        if (s.doneBy === 'visit') return date ? `${t('programmes.evidence_visit')} · ${date}` : t('programmes.evidence_visit');
        return date ? t('programmes.stop_done_on', { date }) : t('programmes.state_done');
    }
    if (s.state === 'booked') {
        const date = formatDay(s.appointment?.date);
        const time = formatClock(s.appointment?.time);
        const when = [date, time].filter(Boolean).join(' · ');
        return when ? t('programmes.stop_booked_for', { when }) : t('programmes.state_booked');
    }
    const range = rangeLabel(s.windowFrom, s.windowTo);
    if (s.state === 'open') return range ? t('programmes.stop_book_now', { range }) : t('programmes.state_open');
    if (s.state === 'late') return range ? t('programmes.stop_late_window', { range }) : t('programmes.state_late');
    const from = formatDay(s.windowFrom || s.dueDate);
    if (s.kind === 'start') return from ? t('programmes.stop_starts_on', { date: from }) : '';
    return from ? t('programmes.stop_opens_on', { date: from }) : '';
};

/**
 * @param programme  a booking Line or a patient programme (timeline)
 * @param stops      already-normalised stops (instead of `programme`)
 * @param testIdPrefix  ids: `${prefix}-row-${key}`, `${prefix}-state-${key}` … — or a
 *                      function (part, key) => id, so a host keeps the ids it
 *                      has always had
 * @param actions    (stop) => node | null
 * @param showNotes  print a hand tick's note (not on the desk projection)
 * @param highlightKey  the stop a booking would take (outlined)
 */
function ProgrammeStopLine({
    programme = null, stops = null, testIdPrefix = 'programme-stop', actions = null, showNotes = true,
    highlightKey = null, className = '',
}) {
    const { t } = useTranslation();
    const list = stops || programmeStops(programme);
    if (!list.length) return null;
    const tid = (part, key) => (typeof testIdPrefix === 'function' ? testIdPrefix(part, key) : `${testIdPrefix}-${part}-${key}`);
    return (
        <ol data-testid={tid('line', 'all')} className={`flex flex-col ${className}`}>
            {list.map((s, i) => {
                const style = STOP_STYLE[s.state] || STOP_STYLE.upcoming;
                const Icon = style.icon;
                const last = i === list.length - 1;
                const emergency = s.appointment?.purpose === 'emergency';
                const rowActions = actions ? actions(s) : null;
                const highlighted = highlightKey && s.key === highlightKey;
                return (
                    <li key={s.key || i} data-testid={tid('row', s.key)} data-state={s.state} className="relative flex gap-3">
                        <div className="flex flex-col items-center">
                            <span className={`mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${style.dot}`} aria-hidden="true">
                                <Icon className="h-3.5 w-3.5" />
                            </span>
                            {!last && <span className="mt-1 w-0.5 flex-1 rounded-full bg-gray-200" aria-hidden="true" />}
                        </div>
                        <div className={`min-w-0 flex-1 ${last ? '' : 'pb-3'}`}>
                            <div className={`rounded-xl border px-3 py-2 ${highlighted ? 'border-[#4ABDBD] bg-[#4ABDBD]/5' : 'border-gray-100 bg-white'}`}>
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                            <span className="text-sm font-semibold text-gray-800">
                                                {stopLabel(s, t)}
                                                {/* The start stop's label already says "Start" —
                                                    its name would read "Start · Start". */}
                                                {s.name && s.kind !== 'start' ? <span className="font-normal text-gray-600"> · {s.name}</span> : null}
                                            </span>
                                            <StopStateChip state={s.state} testId={tid('state', s.key)} />
                                        </div>
                                        <p data-testid={tid('detail', s.key)} className="mt-0.5 text-[11px] text-gray-500">{stopDetail(s, t)}</p>
                                        {emergency && (
                                            <p data-testid={tid('emergency', s.key)} className="mt-1 inline-flex items-start gap-1 rounded-lg bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                                                <Siren className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
                                                {s.appointment?.emergencyReason
                                                    ? t('programmes.emergency_reason_line', { reason: s.appointment.emergencyReason })
                                                    : t('programmes.purpose_emergency_short')}
                                            </p>
                                        )}
                                        {showNotes && s.note && (
                                            <p className="mt-0.5 flex items-start gap-1 text-[11px] text-gray-600">
                                                <StickyNote className="mt-px h-3 w-3 shrink-0 text-gray-400" aria-hidden="true" />
                                                {s.note}
                                            </p>
                                        )}
                                    </div>
                                    {rowActions ? <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">{rowActions}</div> : null}
                                </div>
                            </div>
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}

/**
 * A linked appointment's programme — "Diabetes care · Stop 2", and the
 * emergency reason — from the appointment's `programme` object. Nothing for
 * an ordinary appointment.
 */
export function AppointmentProgrammeTag({ programme, className = '', testId = 'appointment-programme-tag' }) {
    const { t } = useTranslation();
    const line = appointmentProgrammeLine(programme, t);
    if (!line) return null;
    const emergency = programme.purpose === 'emergency';
    return (
        <div data-testid={testId} className={`flex flex-wrap items-center justify-center gap-1.5 ${className}`}>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#4ABDBD]/10 px-3 py-1 text-xs font-semibold text-[#2D8A8A]">
                <RouteIcon className="h-3.5 w-3.5" aria-hidden="true" />
                <span data-testid={`${testId}-line`}>{line}</span>
            </span>
            {emergency && (
                <span data-testid={`${testId}-emergency`} className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">
                    <Siren className="h-3.5 w-3.5" aria-hidden="true" />
                    {programme.emergencyReason
                        ? t('programmes.emergency_reason_line', { reason: programme.emergencyReason })
                        : t('programmes.purpose_emergency_short')}
                </span>
            )}
        </div>
    );
}

export default ProgrammeStopLine;
